import { EditVideoContent, changeContentstatus, createAssessmentAPI, createDocument, createVideoAPI, deleteContentById, editAssessment, editDocument, getCourseModuleContent, updateContentOrder } from '../../../services/adminService';
import React, { useEffect, useRef, useState } from 'react';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Reorder } from 'framer-motion';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import Loader from '../../../components/admin/Loader';
import AdminPage from '../../../components/admin/AdminPage';
import NoData from "../../../components/common/NoData";
import { usePermissions } from '../../../context/PermissionsContext';
import ScormUploadModal from './ScormUploadModal';


function ModuleContent() {
    const navigate = useNavigate();
    const { courseId, moduleId } = useParams();
    const decodedCourseId = decodeData(courseId);
    const decodedModuleId = decodeData(moduleId);
    const { permissions } = usePermissions();

    const uploadBaselineCountRef = useRef(0);
    const uploadExpectedVideoIdRef = useRef(null);
    const uploadProcessingSinceRef = useRef(null);
    const uploadPollTimeoutRef = useRef(null);
    const uploadProcessingTimeoutRef = useRef(null);


    // Group all useState declarations at the top
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contents, setContents] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showScormModal, setShowScormModal] = useState(false);
    const [contentToDelete, setContentToDelete] = useState(null);
    const [statusLoading, setStatusLoading] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [draggedItem, setDraggedItem] = useState(null);
    const [isReordering, setIsReordering] = useState(false);
    const [selectedContentType, setSelectedContentType] = useState('video');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    const [useVideoUrl, setUseVideoUrl] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingContent, setIsDraggingContent] = useState(false);
    const [classroomName, setClassroomName] = useState(null);
    const [moduleName, setModuleName] = useState(null);
    const [errors, setErrors] = useState({
        videoName: '',
        videoDescription: '',
        videoUrl: '',
        videoFile: '',
        videoEarningPoints: '',
        documentName: '',
        documentFile: '',
        documentEarningPoints: '',
        assessmentName: '',
        assessmentDuration: '',
        assessmentPassingPercentage: '',
        assessmentRewardPoints: '',
        surveyName: '',
        surveyRewardPoints: ''
    });

    // Upload progress modal states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('uploading'); // 'uploading', 'success', 'error'
    const [uploadProgress, setUploadProgress] = useState(0);

    const [videoForm, setVideoForm] = useState({
        name: '',
        description: '',
        videoUrl: '',
        videoDuration: '',
        videoFile: null,
        videoPreview: null,
        earningPoints: '0'
    });

    const [documentForm, setDocumentForm] = useState({
        name: '',
        description: '',
        documentFile: null,
        documentPreview: null,
        earningPoints: '0'
    });

    const [assessmentForm, setAssessmentForm] = useState({
        name: '',
        duration: '',
        passingPercentage: '',
        rewardPoints: '0'
    });

    const [surveyForm, setSurveyForm] = useState({
        name: '',
        rewardPoints: '0'
    });

    const [contentSummary, setContentSummary] = useState({
        videos: 0,
        documents: 0,
        assessments: 0,
        surveys: 0,
        scorm: 0,
        totalDuration: '00:00:00'
    });

    const contentTypeOptions = [
        {
            id: 'video',
            label: 'Video lesson',
            description: 'Upload or link a training video for this module.',
            icon: 'fluent:video-clip-24-regular'
        },
        {
            id: 'document',
            label: 'Document',
            description: 'Attach supporting PDFs or reading material.',
            icon: 'fluent:document-24-regular'
        },
        {
            id: 'assessment',
            label: 'Assessment',
            description: 'Add questions to check understanding.',
            icon: 'fluent:quiz-new-24-regular'
        },
        {
            id: 'survey',
            label: 'Survey',
            description: 'Collect feedback or reflections from trainees.',
            icon: 'fluent:form-24-regular'
        },
        {
            id: 'scorm',
            label: 'SCORM Package',
            description: 'Upload an interactive SCORM ZIP package.',
            icon: 'fluent:archive-24-regular'
        }
    ];

    const getPdfViewerUrl = (url) => {
        if (!url) return '';
        return url.includes('#') ? url : `${url}#toolbar=0&navpanes=0&scrollbar=0`;
    };

    // Ensure unique contents and then filter based on search query
    const filteredContents = React.useMemo(() => {
        const seenKeys = new Set();
        const unique = contents.filter(item => {
            const uniqueKey = `${item.type}-${item.id}`;
            if (seenKeys.has(uniqueKey)) return false;
            seenKeys.add(uniqueKey);
            return true;
        });
        return unique.filter(content =>
            content.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [contents, searchQuery]);

    // Debug logging

    useEffect(() => {
        fetchModuleContent();
    }, []);

    useEffect(() => {
        if (!showUploadModal || uploadStatus !== 'uploading') return;

        if (uploadProcessingSinceRef.current && !uploadProcessingTimeoutRef.current) {
            uploadProcessingTimeoutRef.current = window.setTimeout(() => {
                setUploadStatus('error');
                toast.error('Video processing is taking longer than expected. Please refresh the page after a few minutes and check again.', {
                    position: 'top-center',
                    autoClose: 6000
                });
            }, 5 * 60 * 1000);
        }

        return () => {
            if (uploadProcessingTimeoutRef.current) {
                window.clearTimeout(uploadProcessingTimeoutRef.current);
                uploadProcessingTimeoutRef.current = null;
            }
        };
    }, [showUploadModal, uploadStatus]);

    useEffect(() => {
        if (showAddModal) {
            setErrors({
                videoName: '',
                videoDescription: '',
                videoUrl: '',
                videoFile: '',
                videoEarningPoints: '',
                documentName: '',
                documentFile: '',
                documentEarningPoints: '',
                assessmentName: '',
                assessmentDuration: '',
                assessmentPassingPercentage: '',
                assessmentRewardPoints: '',
                surveyName: '',
                surveyRewardPoints: ''
            });
        }
    }, [showAddModal]);

    const fetchModuleContent = async () => {
        try {
            setLoading(true);
            const response = await getCourseModuleContent({ module_id: decodedModuleId });
            // console.log('Module Content Response -------', response);
            setClassroomName(response.course_name);
            setModuleName(response?.module_name || response?.data?.module_name || response?.data?.module_name_text || null);
            
            if (response.success) {
                const formattedContents = response.data.response.map(item => ({
                    id: item.id,
                    type: item.type,
                    title: item.title,
                    description: item.type === 'video' ? (item.description || 'No description available') : '',
                    url: item.url || item.attachment_detail,
                    duration: item.duration || 'N/A',
                    date: new Date(item.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                    }),
                    status: item.is_active ? 'active' : 'inactive',
                    serial_no: item.serial_no,
                    // Video specific fields
                    comments: item.total_comments || 0,
                    rating: 4.5, // Default rating since not in API
                    totalRatings: 128, // Default since not in API
                    // Assessment specific fields
                    totalQuestions: item.total_questions || 0,
                    timeLimit: item.duration ? `${item.duration} minutes` : 'No time limit',
                    passingPercentage: item.pass_percentage || 0,
                    earnPoint: item.earn_point || 0,
                    // Document specific fields
                    fileSize: item.total_size_mb ? `${item.total_size_mb} MB` : 'Unknown size',
                    fileType: item.url ? item.url.split('.').pop().toUpperCase() : 'FILE',
                    // Survey specific fields
                    estimatedTime: item.duration && item.duration > 0 ? `${item.duration} minutes` : 'No time limit',
                }));
                
                // Calculate content summary
                const summary = formattedContents.reduce((acc, item) => {
                    switch (item.type) {
                        case 'video':
                            acc.videos++;
                            break;
                        case 'document':
                            acc.documents++;
                            break;
                        case 'assessment':
                            acc.assessments++;
                            break;
                        case 'survey':
                            acc.surveys++;
                            break;
                        case 'scorm':
                            acc.scorm++;
                            break;
                    }
                    return acc;
                }, {
                    videos: 0,
                    documents: 0,
                    assessments: 0,
                    surveys: 0,
                    scorm: 0,
                    totalDuration: '00:00:00'
                });

                setContentSummary(summary);
                // Sort by serial_no
                formattedContents.sort((a, b) => a.serial_no - b.serial_no);
                setContents(formattedContents);
                return formattedContents;
            } else {
                setContents([]);
                setContentSummary({
                    videos: 0,
                    documents: 0,
                    assessments: 0,
                    surveys: 0,
                    scorm: 0,
                    totalDuration: '00:00:00'
                });
                toast.error(response.message || 'Failed to fetch module content', {
                    position: 'top-center',
                    autoClose: 3000
                });
                return [];
            }
        } catch (error) {
            // console.log('error-------', error);
            setContents([]);
            setContentSummary({
                videos: 0,
                documents: 0,
                assessments: 0,
                surveys: 0,
                totalDuration: '00:00:00'
            });
            toast.error('Error fetching module content: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Framer Motion drag and drop handlers
    const handleReorderContents = async (newOrder) => {
        // Update local state immediately for smooth UI
        setContents(newOrder);
        setIsReordering(true);

        try {
            // Prepare ordered items for backend
            const orderedItems = newOrder.map(item => ({
                id: item.id,
                table_name: item.type === 'video' ? 'videos' :
                           item.type === 'document' ? 'documents' :
                           item.type === 'assessment' ? 'assessments' :
                           item.type === 'survey' ? 'assessments' :
                           item.type === 'scorm' ? 'scorm_content' : 'videos'
            }));

            const response = await updateContentOrder({
                module_id: decodedModuleId,
                ordered_items: orderedItems
            });

            if (response?.success) {
                toast.success('Content reordered successfully!', {
                    position: 'top-center',
                    autoClose: 3000
                });
            } else {
                throw new Error(response?.message || 'Failed to reorder content');
            }
        } catch (error) {
            console.error('❌ Error reordering content:', error);
            toast.error('Failed to save order. Please try again.', {
                position: 'top-center',
                autoClose: 3000
            });
            // Revert to server order by refetching
            fetchModuleContent();
        } finally {
            setIsReordering(false);
        }
    };

    // Separate handlers for file upload drag and drop
    const handleFileDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleFileDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const file = e.dataTransfer.files[0];
        if (selectedContentType === 'video') {
            handleVideoFile(file);
        } else if (selectedContentType === 'document') {
            handleDocumentFile(file);
        }
    };

    const handleContentClick = (content) => {
        if (isDraggingContent) return; // Prevent opening content while dragging

        // Encode all values once
        const encodedContentId = encodeData(content.id);
        const encodedCourseId = encodeData(decodedCourseId);
        const encodedModuleId = encodeData(decodedModuleId);
    
        // Predefined routes
        const videoUrl = `/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}/video/${encodedContentId}`;
        const documentUrl = `/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}/document/${encodedContentId}`;
        const assessmentUrl = `/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}/quiz/${encodedContentId}`;
        const surveyUrl = `/admin/courses/module/${encodedCourseId}/content/${encodedModuleId}/survey/${encodedContentId}`;
    
        // Navigate based on content type
        switch (content.type) {
            case 'video':
                navigate(videoUrl);
                break;
            case 'document':
                navigate(documentUrl, {
                    state: {
                        documentName: content.title,
                        pdfUrl: content.url
                    }
                });
                break;
            case 'assessment':
                navigate(assessmentUrl);
                break;
            case 'survey':
                navigate(surveyUrl);
                break;
            default:
                break;
        }
    };
    

    const handleVideoFormChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file' && files[0]) {
            handleVideoFile(files[0]);
        } else {
            setVideoForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleVideoFile = (file) => {
        if (file && file.type.startsWith('video/')) {
                    if (file.size > 350 * 1024 * 1024) { // 350MB
            toast.error('File size should not exceed 350MB', {
                    position: 'top-center',
                    autoClose: 3000
                });
                return;
            }

            // Create video element to get duration
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                // Convert duration to minutes:seconds format
                const minutes = Math.floor(video.duration / 60);
                const seconds = Math.floor(video.duration % 60);
                const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                
                setVideoForm(prev => ({
                    ...prev,
                    videoFile: file,
                    videoPreview: URL.createObjectURL(file),
                    videoDuration: formattedDuration
                }));
            };

            video.src = URL.createObjectURL(file);
        } else {
            alert('Please upload a valid video file');
        }
    };

    const handleRemoveVideo = () => {
        if (videoForm.videoPreview) {
            URL.revokeObjectURL(videoForm.videoPreview);
        }
        setVideoForm(prev => ({
            ...prev,
            videoFile: null,
            videoPreview: null,
            videoDuration: ''
        }));
    };

    const handleDocumentFormChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file' && files[0]) {
            handleDocumentFile(files[0]);
        } else {
            setDocumentForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDocumentFile = (file) => {
        if (file && file.type === 'application/pdf') {
            if (file.size > 10 * 1024 * 1024) { // 10MB
                toast.error('File size should not exceed 10MB', {
                    position: 'top-center',
                    autoClose: 3000
                });
                return;
            }
            setDocumentForm(prev => ({
                ...prev,
                documentFile: file,
                documentPreview: URL.createObjectURL(file)
            }));
        } else {
            toast.error('Please upload a PDF file only', {
                position: 'top-center',
                autoClose: 3000
            });
        }
    };

    const handleRemoveDocument = () => {
        if (documentForm.documentPreview) {
            URL.revokeObjectURL(documentForm.documentPreview);
        }
        setDocumentForm(prev => ({
            ...prev,
            documentFile: null,
            documentPreview: null
        }));
    };

    const handleAssessmentFormChange = (e) => {
        const { name, value } = e.target;
        setAssessmentForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSurveyFormChange = (e) => {
        const { name, value } = e.target;
        setSurveyForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const newErrors = { ...errors };
        let isValid = true;

        // Reset all errors first
        Object.keys(newErrors).forEach(key => newErrors[key] = '');

        switch (selectedContentType) {
            case 'video':
                if (!videoForm.name.trim()) {
                    newErrors.videoName = 'Video Name is required';
                    isValid = false;
                } else if (videoForm.name.trim().length > 100) {
                    newErrors.videoName = 'Video Name cannot exceed 100 characters';
                    isValid = false;
                }

                if (!videoForm.description.trim()) {
                    newErrors.videoDescription = 'Video Description is required';
                    isValid = false;
                } else if (videoForm.description.trim().length > 500) {
                    newErrors.videoDescription = 'Video Description cannot exceed 500 characters';
                    isValid = false;
                }

                if (useVideoUrl) {
                    if (!videoForm.videoUrl.trim()) {
                        newErrors.videoUrl = 'Video URL is required';
                        isValid = false;
                    }
                } else if (!videoForm.videoFile && !isEditMode) {
                    newErrors.videoFile = 'Please upload a video file';
                    isValid = false;
                }
                break;

            case 'document':
                if (!documentForm.name.trim()) {
                    newErrors.documentName = 'Document Name is required';
                    isValid = false;
                } else if (documentForm.name.trim().length > 100) {
                    newErrors.documentName = 'Document Name cannot exceed 100 characters';
                    isValid = false;
                }

                if (!documentForm.documentFile && !isEditMode) {
                    newErrors.documentFile = 'Please upload a document file';
                    isValid = false;
                }
                break;

            case 'assessment':
                if (!assessmentForm.name.trim()) {
                    newErrors.assessmentName = 'Assessment Name is required';
                    isValid = false;
                } else if (assessmentForm.name.trim().length > 100) {
                    newErrors.assessmentName = 'Assessment Name cannot exceed 100 characters';
                    isValid = false;
                }

                if (!assessmentForm.duration) {
                    newErrors.assessmentDuration = 'Duration is required';
                    isValid = false;
                } else if (parseInt(assessmentForm.duration) <= 0) {
                    newErrors.assessmentDuration = 'Duration must be greater than 0';
                    isValid = false;
                }

                if (!assessmentForm.passingPercentage) {
                    newErrors.assessmentPassingPercentage = 'Passing Percentage is required';
                    isValid = false;
                } else if (parseInt(assessmentForm.passingPercentage) < 0 || parseInt(assessmentForm.passingPercentage) > 100) {
                    newErrors.assessmentPassingPercentage = 'Passing Percentage must be between 0 and 100';
                    isValid = false;
                }
                break;

            case 'survey':
                if (!surveyForm.name.trim()) {
                    newErrors.surveyName = 'Survey Name is required';
                    isValid = false;
                } else if (surveyForm.name.trim().length > 100) {
                    newErrors.surveyName = 'Survey Name cannot exceed 100 characters';
                    isValid = false;
                }
                break;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleAddContent = async () => {
        if (!permissions.course_module_content_management_create) {
            toast.warning("You don't have permission to create content");
            return;
        }

        if (!validateForm()) {
            return;
        }
        try {
            setIsSubmitting(true);

            if (selectedContentType === 'video') {
                // Show upload modal for video uploads
                setShowUploadModal(true);
                setUploadStatus('uploading');
                setUploadProgress(0);
                uploadBaselineCountRef.current = contents.length;
                uploadExpectedVideoIdRef.current = isEditMode ? editingContent?.id : null;
                uploadProcessingSinceRef.current = null;
                if (uploadPollTimeoutRef.current) {
                    window.clearTimeout(uploadPollTimeoutRef.current);
                    uploadPollTimeoutRef.current = null;
                }
                if (uploadProcessingTimeoutRef.current) {
                    window.clearTimeout(uploadProcessingTimeoutRef.current);
                    uploadProcessingTimeoutRef.current = null;
                }
                if (!videoForm.name) {
                    toast.error('Please enter video name', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                if (useVideoUrl && !videoForm.videoUrl) {
                    toast.error('Please enter video URL', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                if (!useVideoUrl && !videoForm.videoFile) {
                    toast.error('Please upload a video file', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                // Additional validation for file size
                if (!useVideoUrl && videoForm.videoFile && videoForm.videoFile.size > 350 * 1024 * 1024) {
                    toast.error('Video file size exceeds 350MB limit', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                const videoData = new FormData();
                videoData.append("video_name", videoForm.name);
                videoData.append("video_desc", videoForm.description);
                videoData.append("module_id", decodedModuleId);
                videoData.append("course_id", decodedCourseId);
                videoData.append("duration", videoForm.videoDuration || '0');
                videoData.append("earning_points", videoForm.earningPoints || '0');
                videoData.append("total_content", contents.length);
                videoData.append("upload_view_type", useVideoUrl ? 'src' : 'uploaded');

                if (useVideoUrl) {
                    videoData.append("src", videoForm.videoUrl);
                } else {
                    videoData.append("video", videoForm.videoFile);
                    videoData.append("attachment_file", videoForm.videoFile);
                }

//                 // Removed multi-line console.log).toFixed(2)}MB` : 'No file'
//                 });

                // Debug FormData contents
                // console.log("FormData contents:");
                for (const [key, value] of videoData.entries()) {
                    if (value instanceof File) {
                        // console.log(`${key}: File - ${value.name} (${(value.size / (1024 * 1024)).toFixed(2)}MB)`);
                    } else {
                        // console.log(`${key}: ${value}`);
                    }
                }

                // Track upload progress with axios progress events
                // Cap at 95% until backend confirms completion (backend processes S3 multipart upload)
                const config = {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        // Cap at 95% - remaining 5% is backend S3 processing
                        const displayProgress = Math.min(percentCompleted, 95);
                        setUploadProgress(displayProgress);

                        if (displayProgress >= 95 && !uploadProcessingSinceRef.current) {
                            uploadProcessingSinceRef.current = Date.now();
                        }
                    }
                };

                let response;
                if (isEditMode) {
                    videoData.append("video_id", editingContent.id);
                    response = await EditVideoContent(videoData, config);
                } else {
                    response = await createVideoAPI(videoData, config);
                }

                if (response.success) {
                    // Set upload status to success
                    setUploadStatus('success');
                    setUploadProgress(100);

                    uploadProcessingSinceRef.current = null;
                    if (uploadProcessingTimeoutRef.current) {
                        window.clearTimeout(uploadProcessingTimeoutRef.current);
                        uploadProcessingTimeoutRef.current = null;
                    }

                    toast.success(`Video ${isEditMode ? 'updated' : 'created'} successfully`, {
                        position: 'top-center',
                        autoClose: 3000
                    });

                    const pollForVideo = async (attempt = 0) => {
                        const maxAttempts = 15;
                        const delayMs = 2000;

                        const latestContents = await fetchModuleContent();

                        const expectedId = uploadExpectedVideoIdRef.current;
                        const baselineCount = uploadBaselineCountRef.current;

                        const hasExpectedEdit = expectedId
                            ? latestContents.some((c) => c?.type === 'video' && c?.id === expectedId && Boolean(c?.url))
                            : false;

                        const hasNewVideo = !expectedId
                            ? latestContents.length > baselineCount
                            : false;

                        if (hasExpectedEdit || hasNewVideo || attempt >= maxAttempts) {
                            return;
                        }

                        uploadPollTimeoutRef.current = window.setTimeout(() => {
                            pollForVideo(attempt + 1);
                        }, delayMs);
                    };

                    pollForVideo(0);

                    // Close the upload modal after a short delay
                    setTimeout(() => {
                        setShowUploadModal(false);
                    }, 2000);
                } else {
                    setUploadStatus('error');
                    setTimeout(() => {
                        setShowUploadModal(false);
                    }, 2000);

                    toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} video`, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            } else if (selectedContentType === 'assessment' || selectedContentType === 'survey') {
                const payload = {
                    assessment_name: selectedContentType === 'assessment' ? assessmentForm.name : surveyForm.name,
                    assessment_id: editingContent?.id,
                    course_id: decodedCourseId,
                    assessment_type: selectedContentType,
                    module_id: decodedModuleId,
                    duration: selectedContentType === 'assessment' ? (parseInt(assessmentForm.duration) || 0) : 0,
                    earn_point: selectedContentType === 'assessment' ?
                        (parseInt(assessmentForm.rewardPoints) || 0) :
                        (parseInt(surveyForm.rewardPoints) || 0),
                    earn_point_applicable_after: 0,
                    pass_percentage: selectedContentType === 'assessment' ? (parseInt(assessmentForm.passingPercentage) || 0) : 0,
                    total_content: contents.length,
                    is_active: isEditMode ? undefined : 1
                };

                // console.log('payload-------', payload);

                let response;
                if (isEditMode) {
                    // For edit mode, add the assessment ID
                    payload.assessment_id = editingContent.id;
                    response = await editAssessment(payload);
                } else {
                    response = await createAssessmentAPI(payload);
                }

                if (response.success) {
                    toast.success(`${selectedContentType === 'assessment' ? 'Assessment' : 'Survey'} ${isEditMode ? 'updated' : 'created'} successfully`, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    fetchModuleContent(); // Refresh the content list
                } else {
                    toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} ${selectedContentType}`, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            } else if (selectedContentType === 'document') {
                // Validation: For create mode, file is required. For edit mode, file is optional
                if (!isEditMode && !documentForm.documentFile) {
                    toast.error('Please upload a document file', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                // Validation: Document name is always required
                if (!documentForm.name || documentForm.name.trim() === '') {
                    toast.error('Please enter document name', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsSubmitting(false);
                    return;
                }

                const formData = new FormData();
                let response;

                if (isEditMode) {
                    // For edit mode, use the expected field names for editDocument API
                    formData.append("doc_id", editingContent.id);
                    formData.append("doc_name", documentForm.name);
                    formData.append("earning_points", documentForm.earningPoints || '0');

                    if (documentForm.documentFile) {
                        formData.append("doc_url", documentForm.documentFile);
                        formData.append("file_changed", "true");
                    } else {
                        // No new file uploaded, keep existing file
                        formData.append("doc_url", editingContent.url || '');
                        formData.append("file_changed", "false");
                    }

                    // Log form data contents for debugging
                    // console.log('Edit Document Form data contents:');
                    for (const [key, value] of formData.entries()) {
                        // console.log(key, typeof value === 'object' ? 'File object' : value);
                    }

                    response = await editDocument(formData);
                } else {
                    // For create mode, use the expected field names for createDocument API
                    formData.append("title", documentForm.name);
                    formData.append("attachment_file", documentForm.documentFile);
                    formData.append("earning_points", documentForm.earningPoints || '0');
                    formData.append("module_id", decodedModuleId);
                    formData.append("course_id", decodedCourseId);
                    formData.append("total_content", contents.length);
                    formData.append("is_active", "1");

                    response = await createDocument(formData);
                }

                if (response.success) {
                    toast.success(`Document ${isEditMode ? 'updated' : 'uploaded'} successfully`, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    fetchModuleContent(); // Refresh the content list
                } else {
                    toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'upload'} document`, {
                        position: 'top-center',
                        autoClose: 3000
                    });
                }
            }
            // Reset forms and close modal
            setShowAddModal(false);
            setIsEditMode(false);
            setEditingContent(null);
            setVideoForm({
                name: '',
                description: '',
                videoUrl: '',
                videoDuration: '',
                videoFile: null,
                videoPreview: null,
                earningPoints: '0'
            });
            setDocumentForm({
                name: '',
                description: '',
                documentFile: null,
                documentPreview: null,
                earningPoints: '0'
            });
            setAssessmentForm({
                name: '',
                duration: '',
                passingPercentage: '',
                rewardPoints: '0'
            });
            setSurveyForm({
                name: '',
                rewardPoints: '0'
            });
            setUseVideoUrl(false);
        } catch (error) {
            console.error('Error creating content:', error);

            // Handle upload modal for video uploads
            if (selectedContentType === 'video') {
                setUploadStatus('error');
                uploadProcessingSinceRef.current = null;
                if (uploadProcessingTimeoutRef.current) {
                    window.clearTimeout(uploadProcessingTimeoutRef.current);
                    uploadProcessingTimeoutRef.current = null;
                }
            }

            // More specific error handling
            let errorMessage = 'Error creating content';
            if (error.response) {
                // Server responded with error status
                console.error('Server error response:', error.response.data);
                if (error.response.status === 413) {
                    errorMessage = 'File size too large. Please use a smaller video file (max 350MB).';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error occurred. Please check your file and try again.';
                } else if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data?.data?.error_msg) {
                    errorMessage = error.response.data.data.error_msg;
                }
            } else if (error.request) {
                // Request was made but no response received
                console.error('No response received:', error.request);
                errorMessage = 'Network error. Please check your connection and try again.';
            } else {
                // Something else happened
                errorMessage = error.message || 'Unknown error occurred';
            }

            toast.error(errorMessage, {
                position: 'top-center',
                autoClose: 5000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (e, content) => {
        e.stopPropagation();
        if (!permissions.course_module_content_management_edit) {
            toast.warning("You don't have permission to edit content");
            return;
        }
        setIsEditMode(true);
        setEditingContent(content);
        setSelectedContentType(content.type);

        // Pre-populate form data based on content type
        if (content.type === 'video') {
            setVideoForm({
                name: content.title,
                description: content.description,
                videoUrl: content.url || '',
                videoDuration: content.duration || '',
                videoFile: null,
                videoPreview: null,
                earningPoints: content.earnPoint || ''
            });
            setUseVideoUrl(!!content.url);
        } else if (content.type === 'document') {
            setDocumentForm({
                name: content.title,
                description: content.description,
                documentFile: null,
                documentPreview: null,
                earningPoints: content.earnPoint || ''
            });
        } else if (content.type === 'assessment') {
            setAssessmentForm({
                name: content.title,
                duration: content.timeLimit ? content.timeLimit.replace(' minutes', '') : '',
                passingPercentage: content.passingPercentage || '',
                rewardPoints: content.earnPoint || ''
            });
        } else if (content.type === 'survey') {
            setSurveyForm({
                name: content.title,
                rewardPoints: content.earnPoint || ''
            });
        }

        setShowAddModal(true);
    };

    const handleDeleteClick = (e, content) => {
        e.stopPropagation();
        if (!permissions.course_module_content_management_delete) {
            toast.warning("You don't have permission to delete content");
            return;
        }
        setContentToDelete(content);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsSubmitting(true);
            const response = await deleteContentById({
                content_id: contentToDelete.id,
                type: contentToDelete.type.toLowerCase()
            });

            if (response.success) {
                toast.success('Content deleted successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                fetchModuleContent(); // Refresh the content list
            } else {
                toast.error(response.message || 'Failed to delete content', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error deleting content:', error);
            toast.error('Error deleting content: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            setIsSubmitting(false);
            setShowDeleteModal(false);
            setContentToDelete(null);
        }
    };

    const handleStatusChange = async (e, content) => {
        e.stopPropagation();
        if (!permissions.course_module_content_management_status) {
            toast.warning("You don't have permission to change content status");
            return;
        }
        try {
            setStatusLoading(content.id); // Set loading state for this specific content

            const params = {
                module_id: decodedModuleId,
                content_id: content.id
            };

            const data = {
                is_active: content.status === 'active' ? false : true,
                content_type: content.type.toLowerCase()
            };

            const response = await changeContentstatus(params, data);

            if (response.success) {
                // Update local state instead of reloading
                setContents(prevContents => 
                    prevContents.map(item => 
                        item.id === content.id 
                            ? { ...item, status: content.status === 'active' ? 'inactive' : 'active' }
                            : item
                    )
                );

                toast.success(`Content ${content.status === 'active' ? 'deactivated' : 'activated'} successfully`, {
                    position: 'top-center',
                    autoClose: 3000
                });
            } else {
                toast.error(response.message || 'Failed to update content status', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error updating content status:', error);
            toast.error('Error updating content status: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            setStatusLoading(null); // Clear loading state
        }
    };



    const resetAllForms = () => {
        setVideoForm({
            name: '',
            description: '',
            videoUrl: '',
            videoDuration: '',
            videoFile: null,
            videoPreview: null,
            earningPoints: ''
        });
        setDocumentForm({
            name: '',
            description: '',
            documentFile: null,
            documentPreview: null,
            earningPoints: ''
        });
        setAssessmentForm({
            name: '',
            duration: '',
            passingPercentage: '',
            rewardPoints: ''
        });
        setSurveyForm({
            name: '',
            rewardPoints: ''
        });
        setSelectedContentType('video');
        setUseVideoUrl(false);
        setErrors({
            videoName: '',
            videoDescription: '',
            videoUrl: '',
            videoFile: '',
            videoEarningPoints: '',
            documentName: '',
            documentFile: '',
            documentEarningPoints: '',
            assessmentName: '',
            assessmentDuration: '',
            assessmentPassingPercentage: '',
            assessmentRewardPoints: '',
            surveyName: '',
            surveyRewardPoints: ''
        });
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setIsEditMode(false);
        setEditingContent(null);
        resetAllForms();
    };

    const renderVideoForm = () => (
        <div className="space-y-5">
            {/* Basic details */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Basic details
                </h4>
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Video Name *</label>
                        <input
                            type="text"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.videoName ? 'border-red-500' : 'border-input-border'}`}
                            name="name"
                            value={videoForm.name}
                            onChange={(e) => {
                                handleVideoFormChange(e);
                                if (errors.videoName) {
                                    setErrors({ ...errors, videoName: '' });
                                }
                            }}
                            placeholder="Enter video name"
                            maxLength={100}
                        />
                        {errors.videoName ? (
                            <div className="text-red-500 text-xs mt-1">{errors.videoName}</div>
                        ) : (
                            <small className="text-gray-500 text-xs">Maximum 100 characters allowed ({100 - videoForm.name.length} remaining)</small>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Video Description *</label>
                        <textarea
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.videoDescription ? 'border-red-500' : 'border-input-border'}`}
                            name="description"
                            value={videoForm.description}
                            onChange={(e) => {
                                handleVideoFormChange(e);
                                if (errors.videoDescription) {
                                    setErrors({ ...errors, videoDescription: '' });
                                }
                            }}
                            rows={3}
                            placeholder="Describe what this video covers"
                            maxLength={500}
                        />
                        {errors.videoDescription ? (
                            <div className="text-red-500 text-xs mt-1">{errors.videoDescription}</div>
                        ) : (
                            <small className="text-gray-500 text-xs">Maximum 500 characters allowed ({500 - videoForm.description.length} remaining)</small>
                        )}
                    </div>
                </div>
            </div>

            {/* Source */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Source
                </h4>

                <div className="flex items-center mb-3">
                    <input
                        type="checkbox"
                        className="mr-2"
                        id="useVideoUrl"
                        checked={useVideoUrl}
                        onChange={(e) => {
                            setUseVideoUrl(e.target.checked);
                            handleRemoveVideo();
                        }}
                    />
                    <label className="text-sm" htmlFor="useVideoUrl">
                        Use video URL instead of upload (Not recommended)
                    </label>
                </div>

                {useVideoUrl ? (
                    <div>
                        <label className="block text-sm font-medium mb-1">Video URL *</label>
                        <input
                            type="url"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.videoUrl ? 'border-red-500' : 'border-input-border'}`}
                            name="videoUrl"
                            value={videoForm.videoUrl}
                            onChange={(e) => {
                                handleVideoFormChange(e);
                                if (errors.videoUrl) {
                                    setErrors({ ...errors, videoUrl: '' });
                                }
                            }}
                            placeholder="Paste a video URL (e.g., from your CDN)"
                        />
                        {errors.videoUrl && <div className="text-red-500 text-xs mt-1">{errors.videoUrl}</div>}
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex gap-2">
                                <Icon icon="fluent:warning-24-regular" className="text-amber-600 shrink-0 mt-0.5" width={16} height={16} />
                                <p className="text-xs text-amber-800 leading-relaxed">
                                    <strong>Disclaimer:</strong> YouTube videos and other external sources may include unintended content and ads that are beyond our control.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <label className="block text-sm font-medium mb-1">Upload Video *</label>
                        {errors.videoFile && !videoForm.videoFile && (
                            <div className="bg-red-50 border border-red-200 text-red-700 py-2 px-3 rounded mb-2 text-xs">
                                {errors.videoFile}
                            </div>
                        )}
                        {!videoForm.videoPreview ? (
                            <div
                                className={`upload-area relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100 ${isDragging ? 'ring-2 ring-primary/40 bg-indigo-50' : ''}`}
                                onDragOver={handleFileDragOver}
                                onDragLeave={handleFileDragLeave}
                                onDrop={handleFileDrop}
                            >
                                <input
                                    type="file"
                                    accept="video/*"
                                    className="hidden"
                                    onChange={(e) => handleVideoFile(e.target.files[0])}
                                    id="videoUpload"
                                />
                                <label htmlFor="videoUpload" className="mb-0 w-full h-full flex flex-col items-center justify-center text-center gap-1">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-1">
                                        <Icon icon="fluent:document-arrow-up-24-regular" width={26} height={26} />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">Drag & drop video or click to browse</span>
                                    <span className="text-xs text-gray-500">
                                        MP4, MOV, or AVI up to 350MB
                                    </span>
                                </label>
                            </div>
                        ) : (
                            <div className="video-preview">
                                <video
                                    src={videoForm.videoPreview}
                                    controls
                                    className="w-full rounded-lg border border-gray-200"
                                    style={{ maxHeight: '220px' }}
                                >
                                    Your browser does not support the video tag.
                                </video>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-1 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-medium text-xs py-1.5 px-3 rounded-lg mt-2"
                                    onClick={handleRemoveVideo}
                                >
                                    <Icon icon="fluent:dismiss-24-regular" width={14} height={14} />
                                    <span>Remove video</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Playback */}
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Video Duration</label>
                    <input
                        type="text"
                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                        name="videoDuration"
                        value={videoForm.videoDuration}
                        onChange={handleVideoFormChange}
                        disabled={!useVideoUrl}
                        placeholder={useVideoUrl ? "Enter duration (e.g., 10:30)" : "Detected automatically from upload"}
                    />
                    {useVideoUrl && (
                        <small className="text-gray-500 text-xs">
                            Enter duration in MM:SS format (e.g., 05:30)
                        </small>
                    )}
                </div>

            </div>
        </div>
    );

    const renderDocumentForm = () => (
        <div className="space-y-5">
            {/* Basic details */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Basic details
                </h4>
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Document Name *</label>
                        <input
                            type="text"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.documentName ? 'border-red-500' : 'border-input-border'}`}
                            name="name"
                            value={documentForm.name}
                            onChange={(e) => {
                                handleDocumentFormChange(e);
                                if (errors.documentName) {
                                    setErrors({...errors, documentName: ''});
                                }
                            }}
                            placeholder="Enter document name"
                            maxLength={100}
                        />
                        {errors.documentName ? (
                            <div className="text-red-500 text-sm mt-1">{errors.documentName}</div>
                        ) : (
                            <small className="text-gray-500 text-sm">
                                Maximum 100 characters allowed ({100 - documentForm.name.length} remaining)
                            </small>
                        )}
                    </div>
                </div>
            </div>

            {/* File upload & preview */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Document file
                </h4>
                <div className="space-y-3">
                    {/* Global hidden input used for both first upload and change */}
                    <input
                        type="file"
                        id="documentUpload"
                        className="hidden"
                        onChange={handleDocumentFormChange}
                        accept=".pdf"
                    />

                    {/* Upload area – only when no document is attached */}
                    {!documentForm.documentPreview && !editingContent?.url && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Upload Document (PDF only) *</label>
                            {errors.documentFile && !documentForm.documentFile && (
                                <div className="bg-red-50 border border-red-200 text-red-700 py-2 px-3 rounded text-xs">
                                    {errors.documentFile}
                                </div>
                            )}
                            <div
                                className={`upload-area relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100 ${isDragging ? 'ring-2 ring-primary/40 bg-indigo-50' : ''}`}
                                onDragOver={handleFileDragOver}
                                onDragLeave={handleFileDragLeave}
                                onDrop={handleFileDrop}
                                onClick={() => document.getElementById('documentUpload').click()}
                            >
                                <div className="flex flex-col items-center justify-center text-center gap-1">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-1">
                                        <Icon icon="fluent:document-pdf-24-regular" width={26} height={26} />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">Drag & drop PDF or click to browse</span>
                                    <span className="text-xs text-gray-500">
                                        Only PDF files up to 10MB are supported
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Document Preview Section with change button when a document exists */}
                    {(documentForm.documentPreview || editingContent?.url) && (
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium">Current Document Preview</label>
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('documentUpload').click()}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary text-primary text-xs font-medium hover:bg-primary/10"
                                >
                                    <Icon icon="fluent:arrow-sync-24-regular" width={16} height={16} />
                                    <span>Change document</span>
                                </button>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                                <div className="p-2">
                                    {documentForm.documentPreview && (
                                        <div>
                                            <iframe
                                                src={getPdfViewerUrl(documentForm.documentPreview)}
                                                style={{ width: '100%', height: '400px', border: 'none' }}
                                                title="Document Preview"
                                            />
                                            <div className="flex justify-end mt-2">
                                                <button
                                                    className="text-red-600 hover:underline font-medium text-xs"
                                                    onClick={handleRemoveDocument}
                                                    type="button"
                                                >
                                                    Remove Document
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {editingContent?.url && !documentForm.documentPreview && (
                                        <div>
                                            <iframe
                                                src={getPdfViewerUrl(editingContent.url)}
                                                style={{ width: '100%', height: '400px', border: 'none' }}
                                                title="Document Preview"
                                            />
                                            <div className="flex justify-end mt-2">
                                                <a 
                                                    href={editingContent.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline font-medium text-xs"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Open in New Tab
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Points field hidden in module modal – points handled automatically */}
        </div>
    );

    const renderAssessmentForm = () => (
        <div className="space-y-5">
            {/* Basic details */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Basic details
                </h4>
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Assessment Name *</label>
                        <input
                            type="text"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.assessmentName ? 'border-red-500' : 'border-input-border'}`}
                            name="name"
                            value={assessmentForm.name}
                            onChange={(e) => {
                                handleAssessmentFormChange(e);
                                if (errors.assessmentName) {
                                    setErrors({...errors, assessmentName: ''});
                                }
                            }}
                            placeholder="Enter assessment name"
                            maxLength={100}
                        />
                        {errors.assessmentName ? (
                            <div className="text-red-500 text-sm mt-1">{errors.assessmentName}</div>
                        ) : (
                            <small className="text-gray-500 text-sm">
                                Maximum 100 characters allowed ({100 - assessmentForm.name.length} remaining)
                            </small>
                        )}
                    </div>
                </div>
            </div>

            {/* Timing & score */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Assessment settings
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Duration (in minutes)</label>
                        <input
                            type="number"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.assessmentDuration ? 'border-red-500' : 'border-input-border'}`}
                            name="duration"
                            value={assessmentForm.duration}
                            onChange={(e) => {
                                handleAssessmentFormChange(e);
                                if (errors.assessmentDuration) {
                                    setErrors({...errors, assessmentDuration: ''});
                                }
                            }}
                            placeholder="Enter duration in minutes"
                            min="1"
                        />
                        {errors.assessmentDuration && (
                            <div className="text-red-500 text-sm mt-1">{errors.assessmentDuration}</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Passing Percentage</label>
                        <div className="flex items-center gap-2">
                            <div style={{ flex: 1, position: 'relative' }}>
                                <input
                                    type="number"
                                    className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.assessmentPassingPercentage ? 'border-red-500' : 'border-input-border'}`}
                                    name="passingPercentage"
                                    value={assessmentForm.passingPercentage}
                                    onChange={(e) => {
                                        handleAssessmentFormChange(e);
                                        if (errors.assessmentPassingPercentage) {
                                            setErrors({...errors, assessmentPassingPercentage: ''});
                                        }
                                    }}
                                    placeholder="Enter passing percentage"
                                    min="0"
                                    max="100"
                                />
                            </div>
                            <div className="border rounded px-2 py-1 text-xs text-gray-600 bg-gray-50">
                                %
                            </div>
                        </div>
                        {errors.assessmentPassingPercentage && (
                            <div className="text-red-500 text-sm mt-1">{errors.assessmentPassingPercentage}</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Points field hidden in module modal – points handled automatically */}
        </div>
    );

    const renderSurveyForm = () => (
        <div className="space-y-5">
            {/* Basic details */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                    Basic details
                </h4>
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Survey Name *</label>
                        <input
                            type="text"
                            className={`w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.surveyName ? 'border-red-500' : 'border-input-border'}`}
                            name="name"
                            value={surveyForm.name}
                            onChange={(e) => {
                                handleSurveyFormChange(e);
                                if (errors.surveyName) {
                                    setErrors({...errors, surveyName: ''});
                                }
                            }}
                            placeholder="Enter survey name"
                            maxLength={100}
                        />
                        {errors.surveyName ? (
                            <div className="text-red-500 text-sm mt-1">{errors.surveyName}</div>
                        ) : (
                            <small className="text-gray-500 text-sm">
                                Maximum 100 characters allowed ({100 - surveyForm.name.length} remaining)
                            </small>
                        )}
                    </div>
                </div>
            </div>

            {/* Points field hidden in module modal – points handled automatically */}
        </div>
    );

    const renderContentMeta = (content) => {
        const baseContainer = "content-meta text-gray-500 text-xs flex flex-wrap items-center gap-x-3 gap-y-1";
        const baseItem = "meta-item inline-flex items-center gap-1";

        switch (content.type) {
            case 'video':
                return (
                    <div className={baseContainer}>
                        {content.comments > 0 && (
                            <div className={baseItem}>
                                <Icon icon="fluent:comment-24-regular" width={14} height={14} />
                                <span>{content.comments}</span>
                            </div>
                        )}
                        <div className={baseItem}>
                            <Icon icon="fluent:clock-24-regular" width={14} height={14} />
                            <span>{content.duration}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            case 'assessment':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:question-circle-24-regular" width={14} height={14} />
                            <span>{content.totalQuestions} Q</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:timer-24-regular" width={14} height={14} />
                            <span>{content.timeLimit}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:trophy-24-regular" width={14} height={14} />
                            <span>{content.passingPercentage}%</span>
                        </div>
                        {content.earnPoint > 0 && (
                            <div className={baseItem}>
                                <Icon icon="fluent:star-24-regular" width={14} height={14} />
                                <span>{content.earnPoint} pts</span>
                            </div>
                        )}
                    </div>
                );
            case 'survey':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:timer-24-regular" width={14} height={14} />
                            <span>{content.estimatedTime}</span>
                        </div>
                        {content.earnPoint > 0 && (
                            <div className={baseItem}>
                                <Icon icon="fluent:star-24-regular" width={14} height={14} />
                                <span>{content.earnPoint} pts</span>
                            </div>
                        )}
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            case 'document':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:document-24-regular" width={14} height={14} />
                            <span>{content.fileType}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            case 'scorm':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:archive-24-regular" width={14} height={14} />
                            <span>SCORM</span>
                        </div>
                        {content.earnPoint > 0 && (
                            <div className={baseItem}>
                                <Icon icon="fluent:star-24-regular" width={14} height={14} />
                                <span>{content.earnPoint} pts</span>
                            </div>
                        )}
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <AdminPage title="Manage Content" description="Loading module content...">
                <div className="flex justify-center items-center min-h-[320px] rounded-2xl bg-gray-50/50 border border-gray-100">
                    <Loader />
                </div>
            </AdminPage>
        );
    }

    return (
        <AdminPage
            back={
                <button
                    type="button"
                    onClick={() => navigate(`/admin/courses/module/${courseId}`)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                    <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
                    Back
                </button>
            }
            title={
                classroomName
                    ? `${classroomName} · ${moduleName || 'Module'} · Content`
                    : 'Manage Content'
            }
            description={classroomName ? `Add and reorder content for this module` : 'Add videos, documents, assessments, and surveys. Drag to reorder.'}
            actions={
                <>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                            if (!permissions.course_module_content_management_create) {
                                toast.warning("You don't have permission to create content");
                                return;
                            }
                            setShowAddModal(true);
                        }}
                        disabled={!permissions.course_module_content_management_create}
                        title={!permissions.course_module_content_management_create ? "You don't have permission to create content" : "Add Content"}
                    >
                        <Icon icon="fluent:add-24-regular" width="18" height="18" />
                        Add Content
                    </button>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#4F6BFF] text-[#4F6BFF] bg-white hover:bg-[#4F6BFF]/5 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                            if (!permissions.course_module_content_management_create) {
                                toast.warning("You don't have permission to create content");
                                return;
                            }
                            setShowScormModal(true);
                        }}
                        disabled={!permissions.course_module_content_management_create}
                        title={!permissions.course_module_content_management_create ? "You don't have permission to create content" : "Add SCORM"}
                    >
                        <Icon icon="fluent:archive-24-regular" width="18" height="18" />
                        Add SCORM
                    </button>
                </>
            }
        >
            {/* Summary + search */}
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm mb-4">
                <div className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                <Icon icon="fluent:book-24-regular" width="18" height="18" className="text-[#4F6BFF]" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-0">Module content</h3>
                                <p className="text-gray-500 text-xs mt-0.5">
                                    {contentSummary.videos > 0 && `${contentSummary.videos} Videos`}
                                    {contentSummary.videos > 0 && (contentSummary.documents > 0 || contentSummary.assessments > 0 || contentSummary.surveys > 0) && ' · '}
                                    {contentSummary.documents > 0 && `${contentSummary.documents} Documents`}
                                    {contentSummary.documents > 0 && (contentSummary.assessments > 0 || contentSummary.surveys > 0) && ' · '}
                                    {contentSummary.assessments > 0 && `${contentSummary.assessments} Assessments`}
                                    {contentSummary.assessments > 0 && (contentSummary.surveys > 0 || contentSummary.scorm > 0) && ' · '}
                                    {contentSummary.surveys > 0 && `${contentSummary.surveys} Surveys`}
                                    {contentSummary.surveys > 0 && contentSummary.scorm > 0 && ' · '}
                                    {contentSummary.scorm > 0 && `${contentSummary.scorm} SCORM`}
                                </p>
                            </div>
                        </div>
                        <div className="w-full sm:max-w-xs">
                            <div className="relative">
                                <Icon
                                    icon="fluent:search-20-regular"
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                />
                                <input
                                    type="text"
                                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF] outline-none transition-all text-sm"
                                    placeholder="Search content..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Cards */}
            {filteredContents.length === 0 ? (
                searchQuery ? (
                    <div className="text-center mt-4">
                        <Icon icon="fluent:search-24-regular" width="48" height="48" className="text-gray-500 mb-2" />
                        <p className="text-gray-500">No content found matching "{searchQuery}"</p>
                    </div>
                ) : (
                    <NoData message="No content found for this module. Click 'Add Content' to create content." />
                )
            ) : (
                <div className="relative">
                    {/* Loading overlay for reordering */}
                    {isReordering && (
                        <div className="absolute w-full h-full flex items-center justify-center"
                             style={{
                                 backgroundColor: 'rgba(255,255,255,0.8)',
                                 zIndex: 1000,
                                 borderRadius: '8px'
                             }}>
                            <div className="flex items-center gap-2">
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary" role="status"></div>
                                <span className="text-primary font-medium">Reordering content...</span>
                            </div>
                        </div>
                    )}

                    {/* Drag instruction */}
                    {filteredContents.length > 1 && (
                        <div className="flex justify-end items-center mb-2">
                            <small className="text-gray-500 text-xs flex items-center gap-1">
                                <Icon icon="fluent:grip-vertical-20-filled" width="14" height="14" />
                                Drag to reorder · {filteredContents.length} items
                            </small>
                        </div>
                    )}

                    <Reorder.Group
                        axis="y"
                        values={filteredContents}
                        onReorder={handleReorderContents}
                        className="list-unstyled"
                        layoutScroll
                        style={{ overflowY: "visible" }}
                    >
                        {filteredContents.map((content, index) => (
                            <Reorder.Item
                                key={`${content.type}-${content.id}`}
                                value={content}
                                onDragStart={() => {
                                    setDraggedItem(`${content.type}-${content.id}`);
                                    setIsDraggingContent(true);
                                }}
                                onDragEnd={() => {
                                    setDraggedItem(null);
                                    setTimeout(() => setIsDraggingContent(false), 100);
                                }}
                                className="list-none mb-2.5 cursor-grab"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    scale: draggedItem === `${content.type}-${content.id}` ? 1.02 : 1
                                }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{
                                    duration: 0.3,
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 30
                                }}
                                whileDrag={{
                                    scale: 1.01,
                                    zIndex: 20
                                }}
                                whileHover={{
                                    scale: 1.01,
                                    transition: { duration: 0.15 }
                                }}
                                layout
                            >
                                <div className="w-full">
                                    <div
                                        className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-gray-200 overflow-hidden cursor-pointer transition-colors"
                                        onClick={() => {
                                            if (!isDraggingContent) {
                                                handleContentClick(content);
                                            }
                                        }}
                                    >
                                        {/* Header: order label + actions */}
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                                            <div className="text-[10px] font-medium text-gray-400">
                                                #{index + 1}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.course_module_content_management_edit ? "You don't have permission to edit content" : "Edit"}
                                                    onClick={(e) => handleEditClick(e, content)}
                                                    disabled={!permissions.course_module_content_management_edit}
                                                >
                                                    <Icon icon="fluent:edit-24-regular" width={14} height={14} />
                                                </button>
                                                <button
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-red-500 text-red-600 hover:bg-red-50 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.course_module_content_management_delete ? "You don't have permission to delete content" : "Delete"}
                                                    onClick={(e) => handleDeleteClick(e, content)}
                                                    disabled={!permissions.course_module_content_management_delete}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" width={14} height={14} />
                                                </button>
                                                <button
                                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-medium border disabled:opacity-50 disabled:cursor-not-allowed ${
                                                        content.status === "active"
                                                            ? "bg-green-500 hover:bg-green-600 text-white border-green-500"
                                                            : "border-yellow-500 text-yellow-700 hover:bg-yellow-50"
                                                    }`}
                                                    title={!permissions.course_module_content_management_status ? "You don't have permission to change status" : `${content.status === "active" ? "Deactivate" : "Activate"} Content`}
                                                    onClick={(e) => handleStatusChange(e, content)}
                                                    disabled={statusLoading === content.id || !permissions.course_module_content_management_status}
                                                >
                                                    {statusLoading === content.id ? (
                                                        <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <Icon
                                                            icon={content.status === "active" ? "fluent:checkmark-circle-24-regular" : "fluent:dismiss-circle-24-regular"}
                                                            width={14}
                                                            height={14}
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Content Body (clickable) */}
                                        <div className="p-3 flex gap-3 items-start cursor-pointer">
                                            {/* Preview */}
                                            <div className="flex-shrink-0">
                                                {content.type === 'document' ? (
                                                    <div className="flex items-center justify-center w-[100px] h-[75px] rounded-lg bg-gray-50 border border-dashed border-gray-200">
                                                        <Icon
                                                            icon={
                                                                content.fileType === 'PDF' ? "fluent:document-pdf-24-regular" :
                                                                content.fileType === 'DOC' || content.fileType === 'DOCX' ? "fluent:document-text-24-regular" :
                                                                "fluent:document-24-regular"
                                                            }
                                                            width={32}
                                                            height={32}
                                                            className="text-primary"
                                                        />
                                                    </div>
                                                ) : content.type === 'video' ? (
                                                    <video
                                                        src={content.url}
                                                        className="w-[120px] h-[75px] rounded-lg object-cover bg-black/5 border border-gray-200"
                                                        controls={false}
                                                        preload="metadata"
                                                    >
                                                        Your browser does not support the video tag.
                                                    </video>
                                                ) : (
                                                    <div className="flex items-center justify-center w-[100px] h-[75px] rounded-lg bg-gray-50 border border-gray-200">
                                                        <Icon
                                                            icon={content.type === 'assessment' ? "fluent:quiz-new-24-regular" : content.type === 'scorm' ? "fluent:archive-24-regular" : "fluent:form-24-regular"}
                                                            width={28}
                                                            height={28}
                                                            className="text-gray-500"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-medium">
                                                        {content.type === 'video' && (
                                                            <>
                                                                <Icon icon="fluent:video-24-regular" width={12} height={12} />
                                                                <span>Video</span>
                                                            </>
                                                        )}
                                                        {content.type === 'survey' && (
                                                            <>
                                                                <Icon icon="fluent:form-24-regular" width={12} height={12} />
                                                                <span>Survey</span>
                                                            </>
                                                        )}
                                                        {content.type === 'assessment' && (
                                                            <>
                                                                <Icon icon="fluent:quiz-new-24-regular" width={12} height={12} />
                                                                <span>Assessment</span>
                                                            </>
                                                        )}
                                                        {content.type === 'document' && (
                                                            <>
                                                                <Icon icon="fluent:document-24-regular" width={12} height={12} />
                                                                <span>Document</span>
                                                            </>
                                                        )}
                                                        {content.type === 'scorm' && (
                                                            <>
                                                                <Icon icon="fluent:archive-24-regular" width={12} height={12} />
                                                                <span>SCORM</span>
                                                            </>
                                                        )}
                                                    </span>
                                                    <span
                                                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                                            content.status === 'active'
                                                                ? 'bg-green-50 text-green-700 border-green-200'
                                                                : 'bg-gray-50 text-gray-600 border-gray-200'
                                                        }`}
                                                    >
                                                        {content.status === 'active' ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                                <h6 className="mb-1 text-sm font-semibold text-gray-900 truncate">
                                                    {content.title}
                                                </h6>
                                                <div className="mt-1">
                                                    {renderContentMeta(content)}
                                                </div>
                                                {content.type === 'video' && content.description && (
                                                    <p className="text-gray-500 text-xs mt-1.5 line-clamp-2">
                                                        {content.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Reorder.Item>
                        ))}
                    </Reorder.Group>
                </div>
            )}

            {showScormModal && (
                <ScormUploadModal
                    moduleId={decodedModuleId}
                    onSuccess={fetchModuleContent}
                    onClose={() => setShowScormModal(false)}
                />
            )}

            {/* Add Content Modal */}
            {showAddModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <div>
                                <h5 className="text-lg font-semibold text-gray-900">
                                    {isEditMode ? 'Edit Content' : 'Add New Content'}
                                </h5>
                                {classroomName && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {classroomName}{moduleName ? ` · ${moduleName}` : ''}{isEditMode && editingContent?.title ? ` · ${editingContent.title}` : ''}
                                    </p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Choose the content type and fill in the details. You can always edit or reorder later.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                                onClick={handleCloseModal}
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width={18} height={18} />
                            </button>
                        </div>

                        <div className="px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                {/* Left: content type picker & tips */}
                                <div className="md:col-span-1 space-y-4">
                                    <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                                        <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-2">
                                            Content type
                                        </p>
                                        <p className="text-xs text-gray-500 mb-3">
                                            Choose what you want to add to this module.
                                        </p>
                                        <div className="space-y-2">
                                            {contentTypeOptions.map((option) => {
                                                const isActive = selectedContentType === option.id;
                                                return (
                                                    <button
                                                        key={option.id}
                                                        type="button"
                                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                                                            isActive
                                                                ? 'border-[#4F6BFF] bg-[#4F6BFF]/5 text-gray-900'
                                                                : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                                                        } ${isEditMode ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
                                                        onClick={() => {
                                                            if (!isEditMode) {
                                                                setSelectedContentType(option.id);
                                                            }
                                                        }}
                                                        disabled={isEditMode}
                                                    >
                                                        <div
                                                            className={`flex items-center justify-center w-9 h-9 rounded-full ${
                                                                isActive
                                                                    ? 'bg-[#4F6BFF]/10 text-[#4F6BFF]'
                                                                    : 'bg-gray-100 text-gray-500'
                                                            }`}
                                                        >
                                                            <Icon icon={option.icon} width={18} height={18} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium truncate">{option.label}</div>
                                                            <p className="text-[11px] text-gray-500 truncate">
                                                                {option.description}
                                                            </p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {isEditMode && (
                                            <p className="mt-3 text-[11px] text-gray-500">
                                                Content type cannot be changed while editing. To switch type, create a new
                                                content item.
                                            </p>
                                        )}
                                    </div>

                                    <div className="hidden md:block bg-white border border-dashed border-gray-200 rounded-xl p-3">
                                        <p className="text-[11px] text-gray-500 flex items-start gap-2">
                                            <Icon
                                                icon="fluent:lightbulb-24-regular"
                                                width={16}
                                                height={16}
                                                className="mt-[2px] text-amber-500"
                                            />
                                            <span>
                                                Keep items focused and concise. You can always reorder, activate or deactivate
                                                content from the module content list.
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Right: dynamic form */}
                                <div className="md:col-span-2">
                                    <div className="bg-white rounded-xl border border-gray-100 p-4">
                                        {selectedContentType === 'video' && renderVideoForm()}
                                        {selectedContentType === 'document' && renderDocumentForm()}
                                        {selectedContentType === 'assessment' && renderAssessmentForm()}
                                        {selectedContentType === 'survey' && renderSurveyForm()}
                                        {selectedContentType === 'scorm' && (
                                            <div className="text-sm text-gray-600 rounded-xl bg-indigo-50 border border-indigo-100 p-4">
                                                Use the dedicated SCORM upload flow. Close this modal and select Add SCORM from the page actions.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                            <button 
                                type="button" 
                                className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleAddContent}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" role="status" aria-hidden="true"></div>
                                        <span>{isEditMode ?
                                            (selectedContentType === 'video' ? 'Updating Video...' : 'Updating Content...') :
                                            (selectedContentType === 'video' ? 'Uploading Video...' : 'Adding Content...')
                                        }</span>
                                    </>
                                ) : (
                                    <span>{isEditMode ? 'Update Content' : 'Add Content'}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h5 className="text-xl font-semibold">Confirm Delete</h5>
                            <button 
                                type="button" 
                                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setContentToDelete(null);
                                }}
                            >&times;</button>
                        </div>
                        <div className="p-6">
                            <p>Are you sure you want to delete this {contentToDelete?.type.toLowerCase()}?</p>
                            <p className="mb-0"><strong>Title:</strong> {contentToDelete?.title}</p>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-2">
                            <button 
                                type="button" 
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setContentToDelete(null);
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleDeleteConfirm}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" role="status" aria-hidden="true"></div>
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <span>Delete</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Progress Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.8)" }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                        <div className="text-center p-4">
                            {uploadStatus === 'uploading' && (
                                <>
                                    <div className="mb-3">
                                        <Icon icon="fluent:cloud-arrow-up-24-regular" width="48" height="48" className="text-primary" />
                                    </div>
                                    <h5 className="mb-3">Uploading Video...</h5>
                                    <div className="mb-3 h-5 bg-gray-200 rounded-full overflow-hidden" style={{ height: '20px' }}>
                                        <div
                                            className="h-full bg-primary transition-all flex items-center justify-center text-white text-sm"
                                            role="progressbar"
                                            style={{ width: `${uploadProgress}%` }}
                                            aria-valuenow={uploadProgress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        >
                                            {uploadProgress}%
                                        </div>
                                    </div>
                                    <p className="text-gray-500 mb-0">{uploadProgress >= 95 ? 'Processing video... Please wait.' : 'Please wait while we process your video...'}</p>
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-sm"
                                            onClick={() => {
                                                if (uploadPollTimeoutRef.current) {
                                                    window.clearTimeout(uploadPollTimeoutRef.current);
                                                    uploadPollTimeoutRef.current = null;
                                                }
                                                if (uploadProcessingTimeoutRef.current) {
                                                    window.clearTimeout(uploadProcessingTimeoutRef.current);
                                                    uploadProcessingTimeoutRef.current = null;
                                                }
                                                uploadProcessingSinceRef.current = null;
                                                setShowUploadModal(false);
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}

                            {uploadStatus === 'success' && (
                                <>
                                    <div className="mb-3">
                                        <Icon icon="fluent:checkmark-circle-24-filled" width="48" height="48" className="text-green-600" />
                                    </div>
                                    <h5 className="text-green-600 mb-3">Upload Successful!</h5>
                                    <p className="text-gray-500 mb-0">Your video has been {isEditMode ? 'updated' : 'uploaded'} successfully.</p>
                                </>
                            )}

                            {uploadStatus === 'error' && (
                                <>
                                    <div className="mb-3">
                                        <Icon icon="fluent:error-circle-24-filled" width="48" height="48" className="text-red-600" />
                                    </div>
                                    <h5 className="text-red-600 mb-3">Upload Failed</h5>
                                    <p className="text-gray-500 mb-0">There was an error uploading your video. Please try again.</p>
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg text-sm"
                                            onClick={() => {
                                                if (uploadPollTimeoutRef.current) {
                                                    window.clearTimeout(uploadPollTimeoutRef.current);
                                                    uploadPollTimeoutRef.current = null;
                                                }
                                                if (uploadProcessingTimeoutRef.current) {
                                                    window.clearTimeout(uploadProcessingTimeoutRef.current);
                                                    uploadProcessingTimeoutRef.current = null;
                                                }
                                                uploadProcessingSinceRef.current = null;
                                                setShowUploadModal(false);
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </AdminPage>
    );
}

export default ModuleContent;
